import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTeacherBookings } from '@/hooks/useTeacherBookings'
import type { TeacherBooking } from '@/services/api/teacher'
import { onCascadeStateChanged } from '@/utils/cascadeEvents'

interface CalendarCardProps {
  className?: string
}

function formatDate(dateStr: string | null | undefined): string {
  // calendar-v2 (5.5): fehlende Daten explizit kennzeichnen statt 1.1.1970
  if (!dateStr) return 'Datum unbekannt'
  try {
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) return 'Datum unbekannt'
    return d.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' })
  } catch {
    return dateStr
  }
}

function formatTime(start: string | null | undefined, end: string | null | undefined): string {
  if (!start && !end) return 'Zeit unbekannt'
  const startTime = start?.slice(0, 5) || '?'
  const endTime = end?.slice(0, 5) || '?'
  return `${startTime}–${endTime}`
}

function BookingItem({ booking }: { booking: TeacherBooking }) {
  const hasDate = Boolean(booking.date)
  const dateDisplay = formatDate(booking.date)
  const timeDisplay = formatTime(booking.start_time, booking.end_time)
  const title = `${booking.subject_name} ${booking.klass_name}`.trim()
  const meta = `Bei ${booking.requester_name}${booking.room_name ? ` · ${booking.room_name}` : ''}`

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-default">
      <div className="w-10 h-10 bg-primary-600 text-white rounded-lg flex items-center justify-center text-xs font-semibold">
        {hasDate ? dateDisplay.split(' ')[0] : '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{title}</div>
        <div className="text-xs text-gray-500 truncate">{meta}</div>
      </div>
      <div className="text-xs text-gray-600 text-right flex-shrink-0">
        <div>{hasDate ? (dateDisplay.split(' ')[1] || dateDisplay) : 'Datum unbekannt'}</div>
        <div>{timeDisplay}</div>
      </div>
    </div>
  )
}

export function CalendarCard({ className }: CalendarCardProps) {
  const [refreshKey, setRefreshKey] = useState(0)
  const navigate = useNavigate()
  const { bookings, isLoading, error, fetchBookings } = useTeacherBookings()

  useEffect(() => {
    fetchBookings()
  }, [fetchBookings, refreshKey])

  useEffect(() => onCascadeStateChanged(() => fetchBookings()), [fetchBookings])

  const handleRefresh = () => {
    setRefreshKey(k => k + 1)
  }

  const count = bookings.length

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-4 ${className || ''}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="h-5 w-40 bg-gray-100 rounded animate-pulse" />
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse mt-1" />
          </div>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-lg">
              <div className="w-10 h-10 bg-gray-100 rounded-lg animate-pulse" />
              <div className="flex-1">
                <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2 mt-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-4 ${className || ''}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Meine Vertretungen</h3>
          </div>
        </div>
        <div className="text-sm text-red-600 p-2">{error}</div>
        <button
          onClick={handleRefresh}
          className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Erneut versuchen
        </button>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className || ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Meine Vertretungen</h3>
          <p className="text-sm text-gray-500">{count} kommende Einsätze</p>
        </div>
        <button
          onClick={handleRefresh}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Aktualisieren"
          title="Aktualisieren"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>

      {!isLoading && bookings.length === 0 && (
        <div className="text-sm text-gray-500 p-2 text-center">
          Keine kommenden Vertretungen
        </div>
      )}

      {!isLoading && bookings.length > 0 && (
        <div className="space-y-2" role="list" aria-label="Kommende Vertretungen">
          {bookings.map(booking => (
            <div key={booking.id} role="listitem">
              <BookingItem booking={booking} />
            </div>
          ))}
        </div>
      )}

      {bookings.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <button
            className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium py-1"
            onClick={() => navigate('/teacher/bookings')}
          >
            Alle anzeigen →
          </button>
        </div>
      )}
    </div>
  )
}
